import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SiteNav from "components/Redesign/SiteNav.js";
import SiteFooter from "components/Redesign/SiteFooter.js";
import SeasonIcon from "components/Redesign/SeasonIcon.js";
import CompassIcon from "components/Redesign/CompassIcon.js";
import InstagramIcon from "components/Redesign/InstagramIcon.js";
import Scale from "views/examples/scale.js";
import heroImg from "assets/img/hero/live-hero.jpg";

const SEASONS = [
  { n: 1, icon: "spring" },
  { n: 2, icon: "summer" },
  { n: 3, icon: "autumn" },
  { n: 4, icon: "winter" },
];

const DACHLISSEN_URL =
  "https://direct.capaz.de/Webdirekt/PublicViewServlet.html?URL=d04f3da3-1b7f-4245-84bd-01ca471bb5fafc02c128-9027-4674-ad61-b155f1adbc68";
// Dasselbe Volk wandert: Winter und Fruehling im Malcantone, ab Sommer auf
// der Alp in Falera. Es gibt daher nur diese eine gemeinsame Waagen-Ansicht.
const FALERA_MALCANTONE_URL =
  "https://direct.capaz.de/Webdirekt/PublicViewServlet.html?URL=22bff7d4-bb9f-4680-97d2-ddf2e05dfd69cc843b33-d261-417b-9471-25af18a37693#JuDatenPublicView";

const SCALES = [
  { key: "Dachlissen", href: DACHLISSEN_URL },
  { key: "MalcantoneFalera", href: FALERA_MALCANTONE_URL },
];

const COMPASS_ITEMS = [
  { n: 1, icon: "rising" },
  { n: 2, icon: "raining" },
  { n: 3, icon: "falling" },
];

const INSTAGRAM_URL = "https://www.instagram.com/honig_traum?igsh=ZHVhcDJzZ3Y4Ynlr&utm_source=qr";
const FLUGLOCH_VIDEO_ID = "NMoQxZU4y_o";
const FLUGLOCH_VIDEO_URL = "https://www.youtube.com/watch?v=" + FLUGLOCH_VIDEO_ID;
// nocookie-Domain: YouTube setzt so erst beim Abspielen Tracking-Cookies.
const FLUGLOCH_EMBED_URL =
  "https://www.youtube-nocookie.com/embed/" + FLUGLOCH_VIDEO_ID + "?rel=0";

function Live() {
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="rd">
      <SiteNav />

      <div className="hero-banner hero-banner-live">
        <img src={heroImg} alt="Bienen auf der Honigwabe" className="hero-banner-img" />
      </div>

      <section className="page-shell">
        <div className="wrap">
          <div className="rd-section-no">{t("shells.Live label")}</div>
          <h1 className="page-shell-h1">{t("shells.Live title")}</h1>
          <p className="page-shell-lede">{t("live.Intro text")}</p>
        </div>
      </section>

      <section className="live-jahr-section">
        <div className="wrap">
          <h2 className="live-h2">{t("live.Imkerjahr heading")}</h2>
          <p className="live-jahr-intro">{t("live.Imkerjahr intro")}</p>

          <div className="live-jahr-table">
            {SEASONS.map(season => (
              <div className="live-jahr-row" key={season.n}>
                <div className="live-jahr-season">
                  <SeasonIcon season={season.icon} size={30} />
                  <div>
                    <div className="live-jahr-season-name">{t("live.Season " + season.n + " name")}</div>
                    <div className="live-jahr-season-subtitle">{t("live.Season " + season.n + " subtitle")}</div>
                  </div>
                </div>
                <p className="live-jahr-body">{t("live.Season " + season.n + " body")}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="live-scales-section">
        <div className="wrap">
          <h2 className="live-h2">{t("live.Scales heading")}</h2>
          <div className="live-scales-body">
            <p>{t("live.Scales body p1")}</p>
            <p>{t("live.Scales body p2")}</p>
          </div>

          <div className="live-scale-charts">
            <Scale />
          </div>

          <div className="live-scales-grid">
            {SCALES.map(scale => (
              <div className="live-scale-card" key={scale.key}>
                <h3>{t("live.Scale " + scale.key + " name")}</h3>
                <p>{t("live.Scale " + scale.key + " desc")}</p>
                <a className="cta-secondary" href={scale.href} target="_blank" rel="noopener noreferrer">
                  {t("live.Scale " + scale.key + " cta")} <span className="arr">→</span>
                </a>
              </div>
            ))}
          </div>

          <div className="live-compass-box">
            <h3>{t("live.Compass heading")}</h3>
            <p className="live-compass-intro">{t("live.Compass intro")}</p>
            <div className="live-compass-list">
              {COMPASS_ITEMS.map(item => (
                <div className="live-compass-item" key={item.n}>
                  <CompassIcon icon={item.icon} className="live-compass-item-icon" />
                  <div className="live-compass-item-text">
                    <div className="live-compass-item-heading">{t("live.Compass " + item.n + " heading")}</div>
                    <p>{t("live.Compass " + item.n + " body")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <a className="cta-secondary live-compass-extra-cta" href="https://thingspeak.mathworks.com/channels/998803/" target="_blank" rel="noopener noreferrer">
            {t("live.Compass extra cta")} <span className="arr">→</span>
          </a>
        </div>
      </section>

      <section className="live-flugloch-section">
        <div className="wrap">
          <h2 className="live-h2">{t("live.Flugloch heading")}</h2>
          <div className="live-flugloch-media">
            <div className="live-flugloch-body">
              <p>{t("live.Flugloch body p1")}</p>
              <p>{t("live.Flugloch body p2")}</p>
              <a className="cta-secondary" href={FLUGLOCH_VIDEO_URL} target="_blank" rel="noopener noreferrer">
                {t("live.Flugloch video cta")} <span className="arr">→</span>
              </a>
            </div>
            <div className="live-flugloch-video">
              <iframe
                src={FLUGLOCH_EMBED_URL}
                title={t("live.Video title")}
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="live-closing-section">
        <div className="wrap">
          <div className="live-closing-grid">
            <div className="live-closing-block">
              <h3>{t("live.Taste heading")}</h3>
              <p>{t("live.Taste body")}</p>
              <Link to="/unser-honig" className="cta-primary">
                {t("live.Taste cta")} <span className="arr">→</span>
              </Link>
            </div>
            <div className="live-closing-block">
              <h3>{t("live.Follow heading")}</h3>
              <p>{t("live.Follow body")}</p>
              <a className="cta-secondary cta-with-icon" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                <span className="cta-icon-label"><InstagramIcon size={18} />{t("live.Follow cta")}</span>
                <span className="arr">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

export default Live;
